// ======================================================================
// File Name       : AuthedUtils
//
// Copyright(C) 2017 NIPPON TELEGRAPH AND TELEPHONE EAST CORPORATION All
// Rights Reserved.
//
// This source code or any portion thereof must not be
// reproduced or used in any manner whatsoever.
// ======================================================================

import { ADMIN_DIRECT_CONTRACT_LOGIN_URL, ADMIN_OEM_LOGIN_URL, CAN_ACCESS, DISABLE, LO_SUPER_USER, READ, SUPERUSER_DIRECT_CONTRACT_LOGIN_URL, SUPERUSER_OEM_LOGIN_URL, SUPER_USER, USAGE_SETTING, WRITE } from '../constants';
import { 
  MANAGEMENT_AUTHORITY_TYPE,
  SUPERUSER_ENTERPRISE_ROLE,
  USER_AUTHORITY_ROLE,
} from '../constants/AuthorityConstant';
import { HIDDEN_STATUS } from '../constants/EnterpriseConstants';
import EXPIRED_TRIAL_NAVIGATOR_CONFIG from '../constants/ExpiredTrialNavigatorConstant';
import FUNCTION_AUTHORITY, { contentMultiLanguageManagement } from '../constants/FunctionAuthConstants';
import * as FunctionConstants from '../constants/FunctionConstants';
import * as ICON from '../constants/IconConstants';
import * as Path from '../constants/PathConstants';
import ROUTE_CONFIG from '../constants/RouteConstants';
import { SERVICE_TYPE } from '../constants/ServiceConstant';
import SUPERUSER_NAVIGATOR_CONFIG from '../constants/SuperUserNavigatorConstants';
import LEFT_NAVIGATOR_CONFIG from '../constants/LeftNavigatorConstants';
import TOP_NAVIGATOR_CONFIG from '../constants/TopNavigatorConstants';
import SUPER_TOP_NAVIGATOR_CONFIG from '../constants/SuperuserTopNavigatorConstants';
import USER_TYPE_FUNCTION_AUTHORITY from '../constants/UserTypeAuthConstants';
import SUPERUSER_TYPE_FUNCTION_AUTHORITY from '../constants/FunctionSuperuserAuthConstants';
import store from '../store';
import { hasProperty } from './index';
import * as FUNCTION from '../constants/FunctionConstants';
import { SUPERUSER_LOGIN_TYPE } from '../constants/CommonConstants';

function getConfig(items, id) {
  if (!items) { return; }

  for (const item of items) {
    // Test current object
    if (item.id === id) { return item; }

    // Test children recursively
    const child = getConfig(item.child, id);
    if (child) { return child; }
  }
}

// Step 10.0.0 START
function updateConfig(items, id, functionPath, functionId) {
  const config = getConfig(items, id);

  if (config) {
    config.path = functionPath;
    config.id = functionId;
  }
}
// Step 10.0.0 END

export function initTopNavigator(userFunctions, authedInfo) {
  const loginedAsSuperUser = authedInfo && !!authedInfo.enterpriseCd;
  let config = null;
  if (loginedAsSuperUser) {
    config = JSON.parse(JSON.stringify(SUPER_TOP_NAVIGATOR_CONFIG));
  } else {
    config = JSON.parse(JSON.stringify(TOP_NAVIGATOR_CONFIG));
  }
  if (loginedAsSuperUser) {
    const superuserMenu = [
      {
        title: 'システム利用実績',
        path: Path.SERVICE_RECORD_COMPANY_LIST,
        id: FunctionConstants.SERVICE_RECORD_COMPANY_LIST,
        icon: ICON.SERVICE_RECORD,
      },
      {
        title: '企業向けお知らせ一覧',
        path: Path.ENTERPRISE_NOTIFICATION_LIST,
        id: FunctionConstants.ENTERPRISE_NOTIFICATION_LIST,
        icon: ICON.NOTIFICATION,
      }
    ];
    getConfig(config, FUNCTION.G_SYSTEM).child.push(...superuserMenu);
    // Step 12.0.0 START
    // 統合ユーザーの場合は「企業切り替え」のメニューがある
    if (authedInfo && authedInfo.userInfo.superuserType !== SUPERUSER_LOGIN_TYPE.INTEGRATION_SUPERUSER) {
      updateConfig(config, FUNCTION.ENTERPRISE_SWITCHING, null, null);
    }
    // Step 12.0.0 END
    // Change path システム利用実績管理 from 企業別 to サービス別
    if (
      authedInfo.serviceCd &&
      authedInfo.enterpriseManagementFunctionAuthorityInfo &&
      authedInfo.enterpriseManagementFunctionAuthorityInfo.enterprisePerformanceManagementAuthority !== READ &&
      authedInfo.managementFunctionAuthorityInfo &&
      authedInfo.managementFunctionAuthorityInfo.performanceManagementAuthority === READ
    ) {
      getConfig(config, FUNCTION.SERVICE_RECORD_COMPANY_LIST).path = Path.SERVICE_RECORD_SERVICE_LIST;
      getConfig(config, FUNCTION.SERVICE_RECORD_COMPANY_LIST).id = FunctionConstants.SERVICE_RECORD_SERVICE_LIST;
    }
    // Step 11.0.0 START
    // if loginSuperUserSecernId is "service_admin_role", display サービスロール only
    if (authedInfo && authedInfo.loginSuperuserEnterpriseRoleSecernId === SUPERUSER_ENTERPRISE_ROLE.SERVICE_ADMIN) {
      updateConfig(
        config,
        FUNCTION.SUPERUSER_ENTERPRISE_ROLE_LIST,
        Path.SUPERUSER_SERVICE_ROLE_LIST,
        FunctionConstants.SUPERUSER_SERVICE_ROLE_LIST,
      );
    }

    // If there is one service only, display サービスTOP, not display 「サービス一覧」and 「サービスグループ」
    if (
      authedInfo.loginSuperuserServiceRoleList &&
      Object.keys(authedInfo.loginSuperuserServiceRoleList).length === 1
    ) {
      // update サービス一覧 to サービスTOP
      const serviceConfig = getConfig(config, FUNCTION.SERVICE_LIST);
      if (serviceConfig) {
        serviceConfig.path = Path.TOP_LIST;
        serviceConfig.id = FunctionConstants.TOP;
        serviceConfig.title = "サービスTOP";
      }
      // remove サービスグループ
      updateConfig(config, FUNCTION.SERVICE_GROUP_LIST, null, null);
    } else if (
      authedInfo.loginSuperuserServiceRoleList &&
      Object.keys(authedInfo.loginSuperuserServiceRoleList).length === 0
    ) {
      updateConfig(config, FUNCTION.SERVICE_LIST, null, null);
      updateConfig(config, FUNCTION.SERVICE_GROUP_LIST, null, null);
    }
    // Step 11.0.0 END
  }

  // Step 12.0.0 START
  if (
    authedInfo &&
    authedInfo.userInfo &&
    authedInfo.userInfo.superuserType !== SUPERUSER_LOGIN_TYPE.NORMAL_SUPERUSER
  ) {
    // 統合rootユーザー, display アカウント管理 and ログアウト only
    updateConfig(config, FUNCTION.CONTACT, null, null);
    updateConfig(config, FUNCTION.FAQ, null, null);
  }
  // Step 12.0.0 END
  const topNavigatorDatas = buildNavigatorData(config, userFunctions, true);
  return topNavigatorDatas;
}

export function initLeftNavigator(userFunctions, authedInfo) {
  const loginedAsSuperUser = authedInfo && !!authedInfo.enterpriseCd;
  const isShowSuperUserInitMenu = loginedAsSuperUser && authedInfo.serviceCd == null;
  if (isShowSuperUserInitMenu) {
    const config = JSON.parse(JSON.stringify(SUPERUSER_NAVIGATOR_CONFIG));
    const leftNavigatorDatas = buildNavigatorData(config, userFunctions, true);
    return leftNavigatorDatas;
  }

  if (authedInfo.isTrialExpire) {
    const config = JSON.parse(JSON.stringify(EXPIRED_TRIAL_NAVIGATOR_CONFIG));
    if (!loginedAsSuperUser) { 
      config[config.findIndex(config => config.id === FUNCTION.ACCOUNT_EDIT)] = null;
    }
    const leftNavigatorDatas = buildNavigatorData(config, userFunctions, true);
    return leftNavigatorDatas;
  }

  // STEP7.0 #3 START
  const config = JSON.parse(JSON.stringify(LEFT_NAVIGATOR_CONFIG));
  // Step 10.0.0 START
  if (
    authedInfo &&
    authedInfo.managementFunctionAuthorityInfo &&
    authedInfo.managementFunctionAuthorityInfo.userfrontManagementAuthority ===
      MANAGEMENT_AUTHORITY_TYPE.REFERENCE_REGISTRATION_EDIT
  ) {
  // Step 10.0.0 END
    // システム
    // Step 10.0.0 START
    if (authedInfo.usageSettingSufCustomize === USAGE_SETTING.UN_USE) {
      getConfig(config, FUNCTION.G_SYSTEM).child.push({
        title: '受講ユーザー画面設定',
        path: Path.USERFRONT_LOGIN_SCREEN_UPDATE,
        id: FunctionConstants.USERFRONT_LOGIN_SCREEN_UPDATE,
        icon: ICON.TV,
      });
    } else {
      getConfig(config, FUNCTION.G_SYSTEM).child.push({
        title: '受講ユーザー画面設定',
        path: Path.COURSE_THUMBNAIL_UPDATE,
        id: FunctionConstants.COURSE_THUMBNAIL_UPDATE,
        icon: ICON.TV,
      });
    }
    // Step 10.0.0 END
  }

  if (loginedAsSuperUser) {
    // Step 10.2.0 START
    if (authedInfo.serviceCd) {
      // Step 12.0.0 TAS-19074 START
      let superuserMenu = []

      if (authedInfo && authedInfo.loginSuperuserServiceRoleList && 
        authedInfo.loginSuperuserServiceRoleList[authedInfo.serviceCd] &&
        authedInfo.loginSuperuserServiceRoleList[authedInfo.serviceCd].superuserServiceRoleSecernId ===
        USER_AUTHORITY_ROLE.ADMIN
      ) {
        superuserMenu.push({
          title: 'メールテンプレート設定',
          path: Path.MAIL_TEMPLATE_USERS,
          id: FunctionConstants.MAIL_TEMPLATE_USERS,
          icon: ICON.MAIL_TEMPLATE_USERS,
        });
      }

      // Add service detail to left menu
      superuserMenu.push(
        {
          title: 'サービス情報',
          path: Path.SERVICE_DETAIL.replace(':id', authedInfo.serviceCd),
          id: FunctionConstants.SERVICE_DETAIL,
          icon: ICON.INFORMATION,
        }
      )
      // Step 12.0.0 TAS-19074 END
      getConfig(config, FUNCTION.G_SYSTEM).child.push(...superuserMenu);
    }
  // Step 10.2.0 END
  } else {
    // Step 9.0.1 START
    if (
      authedInfo &&
      authedInfo.managementFunctionAuthorityInfo &&
      authedInfo.managementFunctionAuthorityInfo.performanceManagementAuthority === MANAGEMENT_AUTHORITY_TYPE.REFERENCE
    ) {
      getConfig(config, FUNCTION.G_SYSTEM).child.push({
        title: 'システム利用実績',
        path: Path.SERVICE_RECORD_SERVICE_LIST,
        id: FunctionConstants.SERVICE_RECORD_SERVICE_LIST,
        icon: ICON.SERVICE_RECORD,
       });

    }
    // Step 9.0.1 END
  }

  if (authedInfo && authedInfo.managementFunctionAuthorityInfo) {
    const { managementFunctionAuthorityInfo: { usageSettingVideo, liveManagementAuthority }, usageSettingLive } = authedInfo;

    if (usageSettingVideo === USAGE_SETTING.UN_USE) {
      getConfig(config, FUNCTION.CONTENT_VIDEO_LIST).id = null;
    }
  }

  if (authedInfo && authedInfo.serviceType === 2) { // コンテンツ配信サービス
    // do not display コミュニケーション menu
    getConfig(config, FUNCTION.G_COMMUNICATION).child = null;

    // Change path ログイン画面 > 各種サムネイル
    if (authedInfo.usageSettingSufCustomize === USAGE_SETTING.UN_USE) {
      // Step 10.0.0 START
      updateConfig(
        config,
        FUNCTION.USERFRONT_LOGIN_SCREEN_UPDATE,
        Path.COURSE_THUMBNAIL_UPDATE,
        FunctionConstants.COURSE_THUMBNAIL_UPDATE
      );
      // Step 10.0.0 END
    }
  } else { // Do not display コンテンツ配信サービス if login normal service
    getConfig(config, FUNCTION.CONTENT_DISTRIBUTION_LIST).id = null;
  }

  // Step 12.0.0 START
  if (
    authedInfo &&
    (!authedInfo.loginSuperuserServiceRoleList ||
      authedInfo?.loginSuperuserServiceRoleList[authedInfo.serviceCd]?.superuserServiceRoleSecernId !==
      USER_AUTHORITY_ROLE.ADMIN || !authedInfo.allGroupOwnerFlag)
  ) {
    updateConfig(config, FUNCTION.CONTENT_GROUP, null, null);
  }
  // Step 12.0.0 END
  const leftNavigatorDatas = buildNavigatorData(config, userFunctions, true);
  // STEP7.0 #3 END
  return leftNavigatorDatas;
}

/**
 * Initialize route
 * @param {Object} containers - Containers user can access
 */
export function initRoute(containers) {
  return Object.keys(ROUTE_CONFIG).reduce((acc, curKey) => {
    const route = ROUTE_CONFIG[curKey];

    const haveChildRoute = Array.isArray(route);
    if (haveChildRoute) {
      const childRoutes = route.reduce((childAcc, curChildRoute) => {
        const { id } = curChildRoute;
        hasProperty(containers, id) && childAcc.push(curChildRoute);
        return childAcc;
      }, []);

      acc = { ...acc, [curKey]: childRoutes };
    } else {
      const { id } = route;
      hasProperty(containers, id) && (acc = { ...acc, [curKey]: { ...route } });
    }

    return acc;
  }, {});
}

export function initUserFunctionByAuthority({
  serviceCd,
  userInfo,
  managementFunctionAuthorityInfo,
  enterpriseManagementFunctionAuthorityInfo,
  serviceType,
  readOnlyFlag,
  isTrialExpire,
  isBelongServiceGroup,
  isBelongUserGroup,
  loginSuperuserEnterpriseRoleSecernId,
  loginSuperuserServiceRoleList,
}) {
  let functions = {};
  const { displayUserType, userCreateType } = userInfo;
  const superUser = [LO_SUPER_USER, SUPER_USER].indexOf(displayUserType) > -1;
  if (!managementFunctionAuthorityInfo) {
    managementFunctionAuthorityInfo = {};
  }
  managementFunctionAuthorityInfo.adaptiveManagement = WRITE;
  if (superUser) {
    if (managementFunctionAuthorityInfo.usageSettingSufCustomize) {
      managementFunctionAuthorityInfo.configFileManagement = WRITE;
    }
    functions = getSuperUserFunction({
      serviceCd,
      displayUserType,
      managementFunctionAuthorityInfo,
      enterpriseManagementFunctionAuthorityInfo,
      serviceType,
      readOnlyFlag,
      isTrialExpire,
      isBelongUserGroup,
      isBelongServiceGroup,
      loginSuperuserEnterpriseRoleSecernId,
      loginSuperuserServiceRoleList,
    });
  } else {
    // sprint_20230508_#19 START
    functions = getUserFunction(
      managementFunctionAuthorityInfo,
      serviceType,
      isTrialExpire,
      isBelongUserGroup,
      isBelongServiceGroup
    );
    // sprint_20230508_#19 END
  }
  // sprint_20230508_#19 START
  if (isTrialExpire) {
    functions[FunctionConstants.USER_REGISTER] = CAN_ACCESS;
    functions[FunctionConstants.USER_DETAIL] = CAN_ACCESS;
    functions[FunctionConstants.USER_LIST] = CAN_ACCESS;
    functions[FunctionConstants.USER_EDIT] = CAN_ACCESS;
    functions[FunctionConstants.USER_IMPORT] = CAN_ACCESS;
    functions[FunctionConstants.MULTI_LANGUAGE_LIST] = CAN_ACCESS;
    functions[FunctionConstants.MULTI_LANGUAGE_EDIT] = CAN_ACCESS;
    functions[FunctionConstants.MULTI_LANGUAGE_PROCESS_DETAIL] = CAN_ACCESS;
    functions[FunctionConstants.MULTI_LANGUAGE_PROCESS_RESULT_LIST] = CAN_ACCESS;
    superUser ? functions[FunctionConstants.ACCOUNT_EDIT] = CAN_ACCESS : functions[FunctionConstants.ACCOUNT_EDIT] = DISABLE;
    functions[FunctionConstants.LOGOUT] = CAN_ACCESS;
    // STEP12.1.0 TAS-16386 START
    functions[FunctionConstants.LIVE_RESERVED_LIST] = DISABLE;
    // STEP12.1.0 TAS-16386 END
    return {
      ...functions,
    };
  }
  // sprint_20230508_#19 END
  return {
    [FunctionConstants.ACCOUNT_EDIT]: CAN_ACCESS,
    [FunctionConstants.LOGOUT]: CAN_ACCESS,
    [FunctionConstants.CONTACT]: CAN_ACCESS,
    // Step 12.0.0 START
    [FunctionConstants.FAQ]: DISABLE,
    [FunctionConstants.ENTERPRISE_SWITCHING]:
      superUser && userInfo.superuserType === SUPERUSER_LOGIN_TYPE.INTEGRATION_SUPERUSER ? CAN_ACCESS : DISABLE,
    [FunctionConstants.CONTENT_GROUP]:
      superUser &&
      loginSuperuserServiceRoleList &&
      loginSuperuserServiceRoleList[serviceCd] &&
      loginSuperuserServiceRoleList[serviceCd].superuserServiceRoleSecernId === USER_AUTHORITY_ROLE.ADMIN
        ? CAN_ACCESS
        : DISABLE,
    [FunctionConstants.MAIL_TEMPLATE_USERS]:
      superUser &&
        loginSuperuserServiceRoleList &&
        loginSuperuserServiceRoleList[serviceCd] &&
        loginSuperuserServiceRoleList[serviceCd].superuserServiceRoleSecernId === USER_AUTHORITY_ROLE.ADMIN
        ? CAN_ACCESS
        : DISABLE,
    // STEP12.1.0 TAS-16386 START
    [FunctionConstants.LIVE_RESERVED_LIST]: DISABLE,
    // STEP12.1.0 TAS-16386 END
    // Step 12.0.0 END
    ...functions,
  };
}

export function canAccess(functionName) {
  const { functions } = store.getState().authed;
  return hasProperty(functions, functionName);
}

export function canAccessRoute(functionName) {
  let isValid = false;
  const { functions } = store.getState().authed;
  if (Object.prototype.hasOwnProperty.call(functions, functionName)) {
    isValid = functions[functionName] === CAN_ACCESS;
  }
  return isValid;
}

function getUserFunction(managementFunctionAuthorityInfo, serviceType, isTrialExpire, isBelongUserGroup, isBelongServiceGroup) {
  let userFunctions = {
    [FunctionConstants.TOP]: CAN_ACCESS,
  };

  /*
   * Filter container by function authority
   */
  for (const functionName in managementFunctionAuthorityInfo) {
    if (Object.prototype.hasOwnProperty.call(managementFunctionAuthorityInfo, functionName)) {
      const authority = managementFunctionAuthorityInfo[functionName];
      const functionAuthority = FUNCTION_AUTHORITY[functionName];
      if (functionAuthority) {
        let functions = FUNCTION_AUTHORITY[functionName][authority];
        if (functions) {
          // Do not display 多言語表示 in case of コンテンツ配信サービス
          if (functionName === 'contentManagementAuthority' && serviceType === SERVICE_TYPE.CONTENT_DISTRIBUTION) {
            functions = functions.reduce((acc, cur) => {
              if (!contentMultiLanguageManagement.contentMultiLanguageManagementAuthorityMap.includes(cur)) {
                acc.push(cur);
              }
              return acc;
            }, []);
          }
          userFunctions = functions.reduce((acc, cur) => {
            acc[cur] = isTrialExpire ? DISABLE : CAN_ACCESS; // // sprint_20230508_#19
            return acc;
          }, userFunctions);
        }
      }
    }
  }

  // Step 10.0.0 START
  if (!isBelongUserGroup || isBelongServiceGroup !== undefined) {
    userFunctions.LOGIN_HISTORY_SERVICE_CSV_DOWNLOAD = CAN_ACCESS;
  }
  // Step 10.0.0 END
  return userFunctions;
}

function getSuperUserFunction({
  serviceCd,
  displayUserType,
  managementFunctionAuthorityInfo,
  enterpriseManagementFunctionAuthorityInfo,
  serviceType,
  readOnlyFlag,
  isTrialExpire,
  isBelongUserGroup,
  isBelongServiceGroup,
  loginSuperuserEnterpriseRoleSecernId,
  loginSuperuserServiceRoleList,
}) {
  // By default top function belong to service
  let superUserFunctions = {
    [FunctionConstants.TOP]: DISABLE,
  };

  // L0 super user and super user can access their own function as default
  const superUserfunctions = [...USER_TYPE_FUNCTION_AUTHORITY[displayUserType]];

  superUserFunctions = superUserfunctions.reduce((acc, cur) => {
    // sprint_20230508_#19 START
    //acc[cur] = isTrialExpire && cur !== FunctionConstants.SERVICE_LOGIN ? DISABLE : CAN_ACCESS;
    acc[cur] = CAN_ACCESS;
    // sprint_20230508_#19 END
    return acc;
  }, superUserFunctions);

  /*
   * Filter container by enterprise function authority
   */
  for (const functionName in enterpriseManagementFunctionAuthorityInfo) {
    if (Object.prototype.hasOwnProperty.call(enterpriseManagementFunctionAuthorityInfo, functionName)) {
      const authority = enterpriseManagementFunctionAuthorityInfo[functionName];
      const functionAuthority = SUPERUSER_TYPE_FUNCTION_AUTHORITY;
      if (functionAuthority) {
        let functions = functionAuthority[functionName][authority];
        if (functions) {
          superUserFunctions = functions.reduce((acc, cur) => {
            if (serviceCd && functionName === 'enterprisePerformanceManagementAuthority') {
              acc[FUNCTION.SERVICE_RECORD_COMPANY_LIST] = CAN_ACCESS;
              if (managementFunctionAuthorityInfo.performanceManagementAuthority
                === MANAGEMENT_AUTHORITY_TYPE.REFERENCE) {
                acc[FUNCTION.SERVICE_RECORD_SERVICE_LIST] = CAN_ACCESS;
                acc[FUNCTION.SERVICE_RECORD_SERVICES_LIST] = CAN_ACCESS; // Step 12.3.0 TAS-21359
              }
              return acc;
            }
            acc[cur] = CAN_ACCESS;
            return acc;
          }, superUserFunctions);
        }
      }
    }
  }

  // All the rest functions which belong to service will be disabled
  // ※ after login to servicerely on management_function_authority_info
  //    decide which function could access
  if (serviceCd) {
    // sprint_20230508_#19 START
    const userContainers = getUserFunction(
      managementFunctionAuthorityInfo,
      serviceType,
      isTrialExpire,
      isBelongUserGroup,
      isBelongServiceGroup
    );
    // sprint_20230508_#19 END
    superUserFunctions = { ...superUserFunctions, ...userContainers };
  } else {
    Object.keys(FUNCTION_AUTHORITY).forEach((functionName) => {
      const functionAuthority = FUNCTION_AUTHORITY[functionName];
      if (functionName !== 'performanceManagementAuthority' && functionName !== 'enterpriseNoticeAuthority') {
        Object.keys(functionAuthority).forEach((authority) => {
          superUserFunctions = functionAuthority[authority].reduce((acc, cur) => {
            acc[cur] = DISABLE;
            return acc;
          }, superUserFunctions);
        });
      }
    });
  }
  // If not SupperUserLO, cannot access register, import of enterprise and service
  const isSupperUserLO = [LO_SUPER_USER].indexOf(displayUserType) > -1;
  if (!isSupperUserLO) {
    superUserFunctions.ENTERPRISE_REGISTER = DISABLE;
    superUserFunctions.ENTERPRISE_IMPORT = DISABLE;
    superUserFunctions.SERVICE_REGISTER = DISABLE;
    superUserFunctions.SERVICE_IMPORT = DISABLE;
  }

  // sprint_20230418 #12 START
  // if superuser (L0/L1) is read-only, cannot access serviceLogin, register, import csv
  if (readOnlyFlag) {
    delete superUserFunctions.SERVICE_LOGIN;
    delete superUserFunctions.ENTERPRISE_REGISTER;
    delete superUserFunctions.ENTERPRISE_EDIT;
    delete superUserFunctions.ENTERPRISE_IMPORT;
    delete superUserFunctions.SERVICE_REGISTER;
    delete superUserFunctions.SERVICE_IMPORT;
    delete superUserFunctions.ENTERPRISE_NOTIFICATION_REGISTER;
    delete superUserFunctions.ENTERPRISE_NOTIFICATION_IMPORT;
    delete superUserFunctions.SUPERUSER_REGISTER;
    delete superUserFunctions.SUPERUSER_IMPORT;
    delete superUserFunctions.NOTIFICATION_REGISTER;
    delete superUserFunctions.NOTIFICATION_IMPORT;
  }
  // sprint_20230418 #12 END

  // Step 10.0.0 START
  if (!isBelongServiceGroup) {
    superUserFunctions.LOGIN_HISTORY_ENTERPRISE_CSV_DOWNLOAD = CAN_ACCESS;
  } else {
    superUserFunctions.SERVICE_GROUP_REGISTER = DISABLE;
    superUserFunctions.SERVICE_GROUP_EDIT = DISABLE;
  }
  // Step 10.0.0 END

  // Step 11.0.0 START
  // if loginSuperUserSecernId is "service_admin_role", use サービスロール only
  if (loginSuperuserEnterpriseRoleSecernId === SUPERUSER_ENTERPRISE_ROLE.SERVICE_ADMIN) {
    superUserFunctions.SUPERUSER_ENTERPRISE_ROLE_LIST = DISABLE;
    superUserFunctions.SUPERUSER_ENTERPRISE_ROLE_DETAIL = DISABLE;
    superUserFunctions.SUPERUSER_ENTERPRISE_ROLE_EDIT = DISABLE;
    superUserFunctions.SUPERUSER_ENTERPRISE_ROLE_REGISTER = DISABLE;
  }

  // in case of there are multiple service and without service management permission, still display サービス一覧
  if (
    loginSuperuserServiceRoleList &&
    Object.keys(loginSuperuserServiceRoleList).length > 1 &&
    enterpriseManagementFunctionAuthorityInfo.serviceManagementAuthority === 0
  ) {
    superUserFunctions.SERVICE_LIST = CAN_ACCESS;
  }
  // Step 11.0.0 END
  return superUserFunctions;
}

function disabledCounter(count, cur) {
  if (cur && cur.disabled) {
    count += 1;
  }
  return count;
}

function buildNavigatorData(menuList = [], userFunctions, isBuildingTop) {
  let userMenuList = menuList.map((item) => {
    let menuItem = { ...item };
    const { id, child } = menuItem;
    let accessStatus = Object.prototype.hasOwnProperty.call(userFunctions, id)
      ? userFunctions[id]
      : false;
    if (isBuildingTop) {
      if (id === FunctionConstants.SERVICE_RECORD_SERVICE_LIST) {
        const companyFunc = FunctionConstants.SERVICE_RECORD_COMPANY_LIST;
        const companyAccess = Object.prototype.hasOwnProperty.call(userFunctions, companyFunc)
        ? userFunctions[id]
        : false;
        if (companyAccess === CAN_ACCESS) {
          menuItem.id = companyFunc;
          menuItem.path = Path.SERVICE_RECORD_COMPANY_LIST;
          accessStatus = companyAccess;
        }
      }
      if (id === FunctionConstants.DOWNLOAD_CONFIG_FILE) {
        const companyFunc = FunctionConstants.DOWNLOAD_CONFIG_FILE;
        const companyAccess = Object.prototype.hasOwnProperty.call(userFunctions, companyFunc)
        ? userFunctions[companyFunc]
        : false;
        if (companyAccess === CAN_ACCESS) {
          menuItem.id = companyFunc;
          menuItem.path = Path.DOWNLOAD_CONFIG_FILE;
          accessStatus = companyAccess;
        }
      }
    }
    const smallMenu = !child;
    if (smallMenu && !accessStatus) {
      return undefined;
    }

    menuItem.disabled = false;
    if (accessStatus === DISABLE) {
      // Show menu but disable
      menuItem.disabled = true;
    }

    // Big menu
    if (!smallMenu) {
      menuItem.child = buildNavigatorData(child, userFunctions, isBuildingTop);

      /*
       * remove menu if submenu is empty, disabled menu if all submenu disabled
       */
      const totalDisabled = menuItem.child.reduce(disabledCounter, 0);

      const totalChildMenu = menuItem.child.length;
      if (totalChildMenu === 0) {
        menuItem = undefined;
      } else if (totalDisabled === totalChildMenu) {
        menuItem.disabled = true;
      }
    }

    return menuItem;
  });

  userMenuList = userMenuList.filter(item => item !== undefined);
  return userMenuList;
}

export function getTopPage() {
  const { authedUser } = store.getState().authed;
  const { readOnlyFlag } = authedUser;

  let topPage = Path.TOP_LIST;
  const loginedAsSuperUser = authedUser && !!authedUser.enterpriseCd;

  if (loginedAsSuperUser) {
    topPage = readOnlyFlag ? Path.ENTERPRISE_LIST : Path.SERVICE_LOGIN_LIST; // sprint_20230418 #12
  }
  return topPage;
}

// Step 9.0.0 START
export function isOEMUser(value) {
  return HIDDEN_STATUS.HIDE_ALL === value;
}

export function getLoginUrl() {
  const { authedUser } = store.getState().authed;
  // Step 11.0.0 START
  return authedUser.isOEMUser ? SUPERUSER_OEM_LOGIN_URL : SUPERUSER_DIRECT_CONTRACT_LOGIN_URL;
  // Step 11.0.0 END
}
// Step 9.0.0 END
